import { AxiosResponse } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import LoadingSpinner from 'components/loading-spinner';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePondsState } from 'views/production-systems/context/ponds-context';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { getTreatmentRegistration, putTreatmentRegistration } from 'views/production-systems/api/productionSystems';

import { Box, Button, CircularProgress } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import DataGridAutocomplete, { Option } from 'components/data-grid/data-grid-autocomplete';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import { renderDataGridOption } from 'components/data-grid/render-data-grid-option';
import dayjs, { Dayjs } from 'dayjs';
import { isValidDecimal } from 'utils/validate-numbers';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import { TreatmentProvider, useTreatmentState } from 'views/production-systems/context/treatment-context';
import { UpdateTreatmentInput } from 'views/production-systems/features/registration-log/types/update-treatment-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import RegistrationHeader from '../../../components/registration-header';

type Props = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type Row = {
  pondId: string;
  pondName: string;
  pondStatus: boolean;
  cycleName: string;
  cycleId: number;
  zoneName: string;
  currentQty: number;
  currentMw: number;
  currentBm: number;
  treatment: { id: string; label: string };
  amount: number;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.treatment.edit';

const EditTreatment: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const { treatments } = useTreatmentState();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, lockPonds, releasePonds, registrationDate } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  // #region STATE
  const [confirm, setConfirm] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(!canEdit);
  const [rows, setRows] = useState<readonly Row[]>([]);
  // end#region STATE

  // #region GET DATA
  const { data: treatment, isFetching } = useGet(
    ['getTreatmentRegistration', globalSiteId, logId, eventNr],
    ({ queryKey }) => getTreatmentRegistration(String(queryKey[1]), Number(queryKey[2]), Number(queryKey[3]))
  );

  useEffect(() => {
    if (treatment && canEdit) lockPonds(treatment.cycles.map((cycle) => cycle.pondId));
  }, [canEdit, lockPonds, treatment]);

  const treatmentOptions = useMemo(() => {
    return (
      treatments
        ?.filter((t) => !t.hidden)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        .map((d) => ({ id: String(d.id), label: d.name })) || []
    );
  }, [treatments]);

  //#region HANDLERS

  const handleSubmit = () => {
    let request = rows.map((row) => ({
      treatmentId: row.treatment!.id,
      amount: row.amount || 0,
      cycleId: row.cycleId,
      pondId: row.pondId,
    }));

    updateTreatmentRegistration.mutate({ cycles: request });
  };

  //#endregion HANDLERS

  //#region  POST DATA

  const updateTreatmentRegistration = usePost<AxiosResponse, UpdateTreatmentInput>(
    (request) => putTreatmentRegistration(globalSiteId, logId, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      !!handleClose && handleClose();
      treatment && releasePonds(treatment.cycles.map((cycle) => cycle.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  //#region HOOKS

  useEffect(() => {
    if (lockedPonds.notGranted.length > 0 && confirm) {
      setReadOnly(true);
    } else {
      setReadOnly(false);
    }
  }, [confirm, lockedPonds.notGranted]);

  //#region COMPONENTS

  const summaryValues: SummaryItem[] = useMemo(() => {
    let newSummary = rows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.currentMw, curr.currentQty);

        return {
          currentQty: prev.currentQty + curr.currentQty,
          currentBm: prev.currentBm + biomass,
          currentMw: 0,
          amount: prev.amount + curr.amount,
        };
      },
      {
        currentQty: 0,
        currentBm: 0,
        currentMw: 0,
        amount: 0,
      }
    );

    newSummary.currentMw = calculateMeanWeight(newSummary.currentBm, newSummary.currentQty);

    return [
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.currentQty,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentMw,
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3,
      },
      {
        value: newSummary.currentBm,
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.amount,
        type: 'number',
        hidden: false,
        minimumFractionDigits: 3,
      },
    ];
  }, [rows]);

  //#endregion SUMMARY

  //#region HOOKS

  const disableSubmit = useMemo(() => {
    return rows.some((row) => row.treatment.id === '' || !isValidDecimal(row.amount)) ? true : false;
  }, [rows]);

  useEffect(() => {
    if (!!treatment) {
      let rows: readonly any[] = treatment.cycles.map((cycle) => {
        let treat = treatments?.find((t) => t.id === cycle.treatmentId);

        return {
          pondId: cycle.pondId,
          pondName: cycle.pondName,
          zoneName: cycle.zoneName,
          cycleId: cycle.cycleId,
          cycleName: cycle.cycleName,
          currentQty: cycle.previousQty,
          currentMw: cycle.previousMw,
          currentBm: calculateBiomass(cycle.previousMw, cycle.previousQty),
          pondStatus: cycle.cycleId !== 0,
          treatment: {
            id: treat?.id,
            label: treat?.name,
          },
          amount: cycle.amount,
        };
      });

      setRows(rows);
    }
  }, [treatment, treatments]);

  //#endregion HOOKS

  //#region DATA GRID

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.destinationPond` }),
        editable: false,
        renderCell: ({ row }) => {
          return (
            <Box display={'flex'} gap={2} alignItems={'center'} width={'100%'}>
              {row.pondStatus ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box overflow={'hidden'} textOverflow={'ellipsis'} title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'cycleName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.cycleName` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'zoneName',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.zone` }),
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'currentQty',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.number` }),
        type: 'number',
        editable: false,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentMw',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.meanWeight` }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentBm',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.biomass` }),
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'treatment',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.treatmentType` }),
        flex: 1,
        type: 'singleSelect',
        cellClassName: readOnly ? 'cell--disabled' : '',
        valueOptions: treatmentOptions,
        valueFormatter: (value: Option) => value?.label,
        renderCell: renderDataGridOption,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value === '';
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-treatment`]: hasError });
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => <DataGridAutocomplete params={params} options={treatmentOptions} />,
        sortable: false,
        editable: !readOnly,
      } as GridColDef<any, { id: number; label: string }, string>,
      {
        field: 'amount',
        flex: 1,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.amount` }),
        cellClassName: readOnly ? 'cell--disabled' : '',
        editable: !readOnly,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-value`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
    ];
  }, [treatmentOptions, readOnly]);

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  //#endregion

  return isFetching ? (
    <LoadingSpinner />
  ) : lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} fromAdd={false} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      {!readOnly && <PondLockTimer handleDialogClose={handleClose} />}
      <RegistrationHeader dateCreated={dateCreated} />
      <DataGrid
        rows={rows}
        apiRef={apiRef}
        columns={columns}
        getRowId={(row) => row.pondId}
        processRowUpdate={processRowUpdate}
        showToolbar
        slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
        slotProps={{
          toolbar: {
            csvOptions: {
              fileName: `${intl.formatMessage({
                id: 'productionSystems.registrationLog.registrationTypes.treatment',
              })}-${dayjs().format('DD/MM/YYYY')}`,
            },
          },
          footer: {
            summaryValues: summaryValues,
          } as DataGridSummaryProps,
        }}
        onCellEditStop={({ field }) => {
          if (field === 'treatment') return;
          handleEditing();
        }}
        onRowClick={({ id }) => {
          const { field } = apiRef.current?.state.tabIndex.cell ?? {};
          if (field === 'treatment') return;
          apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
        }}
      />
      {!readOnly && (
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            disabled={updateTreatmentRegistration.isPending || disableSubmit || isEditing || hasError}
            onClick={handleSubmit}
            startIcon={updateTreatmentRegistration.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
            <FormattedMessage id={'general.confirm'} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

const TreatmentContainer: React.FC<Props> = ({ ...props }) => {
  return (
    <TreatmentProvider>
      <EditTreatment {...props} />
    </TreatmentProvider>
  );
};

export default TreatmentContainer;
